/**
 * FinancialDashboardPage — Dashboard financier
 * CA (factures Meeko) vs Charges RH par structure et par mois.
 * Accès admin uniquement.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, ChevronLeft, ChevronRight, Upload, Trash2,
  AlertCircle, CheckCircle, X, FileSpreadsheet, RefreshCw,
} from 'lucide-react';
import {
  getFinancialDashboard,
  getFinancialImportBatches,
  deleteFinancialImportBatch,
  previewFinancialImport,
  importFinancialInvoices,
  getStructures,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n, d) => d ? `${((n / d) * 100).toFixed(0)} %` : '—';

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, color = 'text-gray-900' }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ─── Import Modal ─────────────────────────────────────────────────────────────

const ImportModal = ({ onClose, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState(1); // 1=upload, 2=mapping, 3=done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rowCount, structureNames }
  const [structures, setStructures] = useState([]);
  const [mapping, setMapping] = useState({}); // { structureName: structureId }
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getStructures().then(res => setStructures(res.data || [])).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setPreview(null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await previewFinancialImport(fd);
      setPreview(res.data);
      // Init mapping : auto-match par nom partiel
      const initMap = {};
      res.data.structureNames.forEach(name => {
        const matched = structures.find(s =>
          s.name.toLowerCase().includes(name.toLowerCase().slice(0, 10)) ||
          name.toLowerCase().includes(s.name.toLowerCase().slice(0, 10))
        );
        initMap[name] = matched ? String(matched.id) : '';
      });
      setMapping(initMap);
      setStep(2);
    } catch (e) {
      console.error('Erreur preview import financier:', e);
      toast.error('Erreur lors de l\'analyse du fichier');
      setError(e.response?.data?.error || 'Erreur d\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping', JSON.stringify(mapping));
      fd.append('replaceExisting', replaceExisting ? 'true' : 'false');
      const res = await importFinancialInvoices(fd);
      setResult(res.data);
      setStep(3);
      toast.success('Factures importees');
    } catch (e) {
      console.error('Erreur import factures:', e);
      toast.error('Erreur lors de l\'import des factures');
      setError(e.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-full';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-primary" />
            Import factures Meeko
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Step 1 — Upload */}
          {step === 1 && (
            <>
              <p className="text-sm text-gray-600">
                Sélectionnez l'extraction factures exportée depuis Meeko
                (fichier .xlsx, toutes structures confondues).
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fichier XLSX *</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
              </div>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="w-full py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition"
              >
                {loading ? 'Analyse en cours...' : 'Analyser le fichier →'}
              </button>
            </>
          )}

          {/* Step 2 — Mapping */}
          {step === 2 && preview && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                {preview.rowCount} ligne{preview.rowCount > 1 ? 's' : ''} détectée{preview.rowCount > 1 ? 's' : ''}.
                Associez chaque nom de structure Meeko à une structure SuiviPlus.
              </div>

              <div className="space-y-2">
                {preview.structureNames.map(name => (
                  <div key={name} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-48 truncate flex-shrink-0" title={name}>{name}</span>
                    <span className="text-gray-400 text-xs">→</span>
                    <select
                      className={inputCls}
                      value={mapping[name] || ''}
                      onChange={e => setMapping(m => ({ ...m, [name]: e.target.value }))}
                    >
                      <option value="">— Ignorer —</option>
                      {structures.map(s => (
                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={replaceExisting}
                  onChange={e => setReplaceExisting(e.target.checked)}
                  className="rounded"
                />
                Remplacer les factures existantes des structures sélectionnées
              </label>

              <div className="flex gap-2">
                <button onClick={() => setStep(1)} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  ← Retour
                </button>
                <button
                  onClick={handleImport}
                  disabled={loading || Object.values(mapping).every(v => !v)}
                  className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition"
                >
                  {loading ? 'Import en cours...' : 'Importer'}
                </button>
              </div>
            </>
          )}

          {/* Step 3 — Done */}
          {step === 3 && result && (
            <>
              <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                <CheckCircle size={18} />
                Import terminé avec succès
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="bg-gray-50 rounded-lg px-3 py-3 border">
                  <div className="text-2xl font-bold text-green-600">{result.imported}</div>
                  <div className="text-xs text-gray-500">Importées</div>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-3 border">
                  <div className="text-2xl font-bold text-gray-400">{result.skipped}</div>
                  <div className="text-xs text-gray-500">Ignorées</div>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-3 border">
                  <div className="text-2xl font-bold text-gray-900">{result.total}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
              </div>
              <button
                onClick={() => { onSuccess(); onClose(); }}
                className="w-full py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition"
              >
                Fermer et actualiser
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

const FinancialDashboardPage = ({ showAllStructures = false }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();
  const isMulti = showAllStructures && structures.length >= 2;
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);
  const [error, setError] = useState(null);
  const [periodMode, setPeriodMode] = useState('year'); // 'year' | 'rolling'
  const [rollingStartMonth, setRollingStartMonth] = useState(9); // Septembre → Août par défaut

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sIds = isMulti ? [] : (currentStructure ? [currentStructure.id] : []);
      let res;
      if (periodMode === 'rolling') {
        const dateFrom = `${year}-${String(rollingStartMonth).padStart(2, '0')}-01`;
        const dateTo = `${year + 1}-${String(rollingStartMonth).padStart(2, '0')}-01`;
        res = await getFinancialDashboard(sIds, null, dateFrom, dateTo);
      } else {
        res = await getFinancialDashboard(sIds, year);
      }
      setData(res.data);
    } catch (e) {
      console.error('Erreur chargement dashboard financier:', e);
      toast.error('Erreur de chargement du dashboard');
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [year, periodMode, rollingStartMonth, isMulti, currentStructure]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await getFinancialImportBatches();
      setBatches(res.data || []);
    } catch (e) {
      console.error('Erreur chargement lots import:', e);
      toast.error('Erreur de chargement des lots d\'import');
      setBatches([]);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Supprimer ce lot d\'import ?')) return;
    try {
      await deleteFinancialImportBatch(batchId);
      toast.success('Lot supprime');
      fetchBatches();
      fetchDashboard();
    } catch (e) {
      console.error('Erreur suppression lot import:', e);
      toast.error('Erreur lors de la suppression du lot');
    }
  };

  // Groupement par structure
  const byStructure = {};
  if (data?.months) {
    for (const m of data.months) {
      if (!byStructure[m.structureId]) {
        byStructure[m.structureId] = { name: m.structureName, months: [] };
      }
      byStructure[m.structureId].months.push(m);
    }
  }

  const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  const endMonthLabel = MONTH_LABELS[(rollingStartMonth - 1 + 11) % 12];
  const endYear = rollingStartMonth === 1 ? year : year + 1;
  const periodLabel = periodMode === 'year'
    ? String(year)
    : `${MONTH_LABELS[rollingStartMonth - 1]} ${year} — ${endMonthLabel} ${endYear}`;

  const getMonthRows = () => Array.from({ length: 12 }, (_, i) => {
    let monthNum, rowYear;
    if (periodMode === 'rolling') {
      const offset = rollingStartMonth - 1;
      const monthIndex = (offset + i) % 12;
      monthNum = monthIndex + 1;
      rowYear = year + Math.floor((offset + i) / 12);
    } else {
      monthNum = i + 1;
      rowYear = year;
    }
    const monthKey = `${rowYear}-${String(monthNum).padStart(2, '0')}`;
    return { monthKey, monthLabel: MONTH_LABELS[monthNum - 1] };
  });

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-primary" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dashboard Financier</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {isMulti ? 'Toutes les structures' : (currentStructure?.name || '')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period mode toggle */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden text-xs font-medium">
            <button
              onClick={() => setPeriodMode('year')}
              className={`px-3 py-2 transition ${periodMode === 'year' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Année civile</button>
            <button
              onClick={() => setPeriodMode('rolling')}
              className={`px-3 py-2 transition border-l border-gray-200 ${periodMode === 'rolling' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >12 mois glissants</button>
          </div>
          {periodMode === 'rolling' && (
            <select
              value={rollingStartMonth}
              onChange={e => setRollingStartMonth(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white"
            >
              {MONTH_LABELS.map((label, i) => (
                <option key={i + 1} value={i + 1}>Début : {label}</option>
              ))}
            </select>
          )}
          {/* Period nav */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <button onClick={() => setYear(y => y - 1)} className="p-1 text-gray-400 hover:text-gray-600">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-900 px-2 whitespace-nowrap">{periodLabel}</span>
            <button onClick={() => setYear(y => y + 1)} className="p-1 text-gray-400 hover:text-gray-600">
              <ChevronRight size={16} />
            </button>
          </div>
          {/* Batches */}
          <button
            onClick={() => { setShowBatches(!showBatches); fetchBatches(); }}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            <Trash2 size={14} />
            Gérer imports
          </button>
          {/* Import */}
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            <Upload size={14} />
            Importer Meeko
          </button>
          <button onClick={fetchDashboard} className="p-2 text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {/* KPI cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="CA Total" value={fmt(data.totals.caTotal)} sub={periodLabel} color="text-primary" />
          <KpiCard label="CA Payé" value={fmt(data.totals.caPayee)} sub={fmtPct(data.totals.caPayee, data.totals.caTotal)} color="text-green-600" />
          <KpiCard label="Non encaissé" value={fmt(data.totals.caImpayee)} sub="impayé + en attente" color="text-red-500" />
          <KpiCard label="Charges RH" value={fmt(data.totals.chargesTotal)} color="text-orange-600" />
        </div>
      )}

      {/* Batches manager */}
      {showBatches && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Lots d'import</h2>
          {batches.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun lot d'import enregistré.</p>
          ) : (
            <div className="space-y-1">
              {batches.map((b, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-700 font-mono text-xs">{b.importBatch}</span>
                  <span className="text-gray-500 text-xs">{b._count?.id} facture{b._count?.id > 1 ? 's' : ''}</span>
                  <button
                    onClick={() => handleDeleteBatch(b.importBatch)}
                    className="text-red-400 hover:text-red-600 ml-3"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tables par structure */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-10">Chargement...</p>
      ) : data?.months?.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileSpreadsheet size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucune donnée pour {periodLabel}</p>
          <p className="text-sm mt-1">Importez un fichier Meeko pour afficher le dashboard.</p>
        </div>
      ) : (
        Object.values(byStructure).map(({ name, months }) => {
          const strTotal = months.reduce((a, m) => ({
            caTotal: a.caTotal + m.caTotal,
            caPayee: a.caPayee + m.caPayee,
            caImpayee: a.caImpayee + m.caImpayee,
            chargesTotal: a.chargesTotal + m.chargesTotal,
          }), { caTotal: 0, caPayee: 0, caImpayee: 0, chargesTotal: 0 });

          // Index par mois
          const byMonth = {};
          months.forEach(m => { byMonth[m.month] = m; });

          return (
            <div key={name} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border-b border-primary/10">
                <h2 className="font-semibold text-gray-900 text-sm">{name}</h2>
                <div className="flex gap-4 text-xs text-gray-500">
                  <span>CA : <strong className="text-gray-900">{fmt(strTotal.caTotal)}</strong></span>
                  <span>Payé : <strong className="text-green-600">{fmt(strTotal.caPayee)}</strong></span>
                  <span>Charges : <strong className="text-orange-600">{fmt(strTotal.chargesTotal)}</strong></span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 text-gray-500 font-medium">Mois</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">CA Total</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Inscriptions</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Familles</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Entreprises</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium text-green-600">Payé</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium text-red-500">Non encaissé</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium text-orange-600">Charges RH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getMonthRows().map(({ monthKey, monthLabel }) => {
                      const m = byMonth[monthKey];
                      if (!m) return (
                        <tr key={monthKey} className="border-b border-gray-50 text-gray-300">
                          <td className="px-4 py-2 font-medium text-gray-400">{monthLabel}</td>
                          <td colSpan={7} className="text-center text-gray-200">—</td>
                        </tr>
                      );
                      return (
                        <tr key={monthKey} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-2 font-semibold text-gray-700">{monthLabel}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">{fmt(m.caTotal)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(m.caInscription)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(m.caFamille)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(m.caEntreprise)}</td>
                          <td className="px-3 py-2 text-right text-green-600 font-medium">{fmt(m.caPayee)}</td>
                          <td className="px-3 py-2 text-right text-red-500">{fmt(m.caImpayee)}</td>
                          <td className="px-3 py-2 text-right text-orange-600">{fmt(m.chargesTotal)}</td>
                        </tr>
                      );
                    })}
                    {/* Total row */}
                    <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                      <td className="px-4 py-2 text-gray-700">Total {periodLabel}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{fmt(strTotal.caTotal)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">—</td>
                      <td className="px-3 py-2 text-right text-gray-600">—</td>
                      <td className="px-3 py-2 text-right text-gray-600">—</td>
                      <td className="px-3 py-2 text-right text-green-600">{fmt(strTotal.caPayee)}</td>
                      <td className="px-3 py-2 text-right text-red-500">{fmt(strTotal.caImpayee)}</td>
                      <td className="px-3 py-2 text-right text-orange-600">{fmt(strTotal.chargesTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onSuccess={fetchDashboard}
        />
      )}
    </div>
  );
};

export default FinancialDashboardPage;
